<script setup lang="ts">
import { Comark } from '@comark/vue'

definePageMeta({ layout: 'text-content-page' })

const { locale, t } = useI18n({
  useScope: 'local'
})

useHead({
  title: t('title'),
  htmlAttrs: {
    lang: locale.value
  },
  meta: [
    {
      name: 'description',
      content: t('description')
    }
  ]
})

const md_de = `# Offene, historische Bahn-Daten

Wir haben für dieses Projekt eine große Menge an Daten gesammelt und zusammengeführt. Die Daten umfassen vergangene Zugverspätungen sowie begleitende Informationen wie Haltestellen und Betreiber. Mit den Daten lassen sich Bahn-Statistiken erstellen, Verspätungen im Nachhinein analysieren bzw. lässt sich alles damit machen, wofür man einen Datensatz über vergangene Zugverspätungen braucht.

Wir teilen diese Verspätungsdaten, um andere Projekte sowie Forschung zu Zugverspätungen zu unterstützen. Neue Projekte verbringen oft den Großteil ihrer Zeit damit, Daten zu sammeln, anstatt nützliche Funktionen zu entwickeln oder zu erforschen. Die Bereitstellung der Daten erfolgt unter dem Motto best effort; wir können keine Vollständigkeit oder Korrektheit garantieren und tragen keinerlei Verantwortung.

Wir teilen die folgenden Datensätze:

## Haltestellen (ODbL)

Wir haben ein Archiv mit Metainformationen über Zughalte gesammelt, einschließlich Geokoordinaten, Namensänderungen und stillgelegter Haltestellen.

::btn-link{href="/open-data/stops" internal=true}
Haltestellen
::

## Operator-Codes

Die Timetables-API (IRIS) der Deutschen Bahn liefert Informationen darüber, welches Unternehmen einen Zug betreibt. Betreiber werden allerdings als Codes zurückgegeben, nicht als menschenlesbare Namen. Wir pflegen eine Zuordnung von Codes zu Namen auf GitLab. Die von HAFAS verwendeten Codes sind nahezu identisch, sodass diese Zuordnung auch nützlich sein kann, wenn Sie die Timetables-API nicht direkt verwenden.

::btn-link{href="https://gitlab.com/bahnvorhersage/iris-owner-matchings"}
<i class="icon icon-gitlab"></i> Operator-Codes
::

## Geparste Zugverspätungen (ODbL) <small class="text-body-secondary">Empfohlen</small>

Geparste Zugverspätungen im Parquet-Format inklusive geschätzter Reisedistanzen. Veröffentlicht in jährlichen Paketen.

::btn-link{href="/open-data/parsed-train-delays" internal=true}
Geparste Zugverspätungen
::

## Rohdaten Zugverspätungen (ODbL) <small class="text-body-secondary">Für Abenteuerlustige</small>

Nahezu Rohdaten zu Zugverspätungen, gesammelt von der Timetables-API (IRIS). verteilt als Brotli-komprimierte CSV-Datenbank-Dumps. Die Daten sind in Fahrpläne und Echtzeitinformationen aufgeteilt. Die Dumps enthalten einige Informationen, die im geparsten Datensatz nicht vorhanden sind, aber Daten aus externen Quellen fehlen. Das Rohdatenformat ist schwierig zu verwenden. Für die meisten Anwendungsfälle empfehlen wir wirklich die Verwendung der schon geparsten Daten.

::btn-link{href="/open-data/raw-data" internal=true}
Rohdaten Zugverspätungen
::

`

const md_en = `# Open Data

We collected and combined a large amount of data for this project and share it to support other projects and research on train delays. New projects often spend most of their time collecting data instead of building or researching useful features. Sharing the data is provided on a best-effort basis; we cannot guarantee its completeness or correctness.

We share the following datasets:

## Stops (ODbL)

We collected an archive of meta information about train stops, including geo coordinates, changes to names and abandoned stops.

::btn-link{href="/open-data/stops" internal=true}
Stops
::

## Operator Codes

The Timetables API (IRIS) from Deutsche Bahn provides information about which company operates a train. Operators are returned as codes, not human-readable names. We maintain a mapping from codes to names on GitLab. The codes used by HAFAS are nearly identical, so this mapping may also be useful if you don't use the Timetables API directly.

::btn-link{href="https://gitlab.com/bahnvorhersage/iris-owner-matchings"}
<i class="icon icon-gitlab"></i> Operator Codes
::

## Parsed Train Delays (ODbL) <small class="text-body-secondary">Recommended</small>

Parsed train delays in the parquet format including estimated travel distances. Available in yearly batches. Click the button for more details and documentation.

::btn-link{href="/open-data/parsed-train-delays" internal=true}
Parsed Train Delays
::

## Raw Collected Train Delays (ODbL) <small class="text-body-secondary">Adventurous</small>

Nearly raw train delay data collected from the Timetables API (IRIS), distributed as Brotli-compressed CSV database dumps. Data is split into schedules and realtime information. The raw dumps contain some information not present in the parsed dataset, but data from external sources is missing. The raw format is difficult to work with, we really recommend using the parsed data for most use cases.

::btn-link{href="/open-data/raw-data" internal=true}
Raw Train Delays
::
`

const md = computed(() => (locale.value === 'de' ? md_de : md_en))
</script>

<template>
  <LocaleSwitcher />
  <Suspense>
    <Comark>{{ md }}</Comark>
  </Suspense>
</template>

<i18n lang="yaml">
de:
  title: Offene, historische Bahn-Daten
  description: Gesammelte Daten aus dem Projekt Bahn-Vorhersage; Vergangene Bahn-Verspätungen.
en:
  title: Open Data
  description: Collected data from the project.
</i18n>
